import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNTUtilsContext } from '../NTUtilsContext';
import { useModuleRegistry, getModules } from './moduleRegistry';

// Define the module interface
export interface RightPanelModule {
  id: string;
  name: string;
  icon: React.ReactNode; // React component instead of string
  iconSize?: number; // Optional icon size. Icons are always square
  component: React.ComponentType<any>;
  props?: Record<string, any>;
  enabled: boolean; // Is this module available in the current context
  expandEnabled: boolean; // Can this module be expanded to full screen
  keyboardShortcut?: string; // Optional keyboard shortcut key
  order?: number; // Optional order for display in the ModuleSwitcher
  transformData?: (rawData: any, selection?: any) => any; // Optional function to transform data for this module
  outsideCall?: boolean; // Optional flag to indicate if the module should be called outside of the right panel
  notListOnModuleBrowser?: boolean; // Optional flag to indicate if the module should not be listed in the module browser. This is a hack for AI Analysis
}

// Context type definition
interface RightPanelContextType {
  // Module registry
  modules: RightPanelModule[];
  registerModule: (module: RightPanelModule) => void;
  unregisterModule: (moduleId: string) => void;
  updateModuleAvailability: (moduleId: string, isEnabled: boolean) => void;
  updateModuleExpandability: (moduleId: string, canExpand: boolean) => void;

  // Active module state
  activeModuleId: string | null;
  setActiveModuleId: (id: string | null) => void;

  // Panel state
  isPanelVisible: boolean;
  setPanelVisible: (visible: boolean) => void;

  // Panel size
  panelSize: number;
  setPanelSize: (size: number) => void;

  // Expanded state of right panel (table compressed)
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;

  // Module-specific panel sizes
  modulePanelSizes: Record<string, number>;
  setModulePanelSize: (moduleId: string, size: number) => void;

  // Get active module
  getActiveModule: () => RightPanelModule | null;

  // Outside call state
  outsideCall: boolean;
  setOutsideCall: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create the context
const RightPanelContext = createContext<RightPanelContextType | undefined>(undefined);

// URL parameter key for active module
const ACTIVE_MODULE_PARAM = 'rightPanelModule';
// const PANEL_SIZE_PARAM = 'rightPanelSize'; // deprecated

// Provider component
export const RightPanelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modules, setModules] = useState<RightPanelModule[]>([]);
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null);
  const { actions } = useNTUtilsContext();
  const [isPanelVisible, setPanelVisible] = useState<boolean>(actions.detailsPanel);
  const [panelSize, setPanelSize] = useState<number>(320); // Default panel width
  const [isExpanded, setIsExpanded] = useState(false);
  const [modulePanelSizes, setModulePanelSizes] = useState<Record<string, number>>({});
  const [outsideCall, setOutsideCall] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Initialize from URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const moduleId = params.get(ACTIVE_MODULE_PARAM);
    // const size = params.get('rightPanelSize'); // deprecated

    if (moduleId) {
      setActiveModuleId(moduleId);
      setPanelVisible(true);
    }

    // if (size) setPanelSize(parseInt(size,10));
  }, []);

  // Keep isPanelVisible in sync with actions.detailsPanel from NTUtilsContext
  // This ensures the panel state is updated when URL parameters change (e.g., after changing report timeframe)
  // Also set a default module when the panel becomes visible but no module is selected
  useEffect(() => {
    setPanelVisible(actions.detailsPanel);
    // When panel becomes visible but no module is selected, default to 'chart'
    if (actions.detailsPanel && !activeModuleId) {
      setActiveModuleId('chart');
    }
  }, [actions.detailsPanel, activeModuleId]);

  // Update URL when active module changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (activeModuleId && isPanelVisible) {
      params.set(ACTIVE_MODULE_PARAM, activeModuleId);
    } else {
      params.delete(ACTIVE_MODULE_PARAM);
    }

    const newSearch = params.toString();
    const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

    // Only update if the path would change
    if (newPath !== `${location.pathname}${location.search}`) {
      navigate(newPath, { replace: true });
    }
  }, [activeModuleId, isPanelVisible, panelSize, location.pathname, location.search]);

  // Register a new module
  const registerModule = (module: RightPanelModule) => {
    // Ensure module has default values for new properties
    const enhancedModule = {
      ...module,
      enabled: module.enabled !== undefined ? module.enabled : true,
      expandEnabled: module.expandEnabled !== undefined ? module.expandEnabled : true,
      icon: module.icon || '📊', // Default icon if not provided
    };

    setModules(prevModules => {
      // Check if module already exists
      const exists = prevModules.some(m => m.id === enhancedModule.id);

      if (exists) {
        // Update existing module
        return prevModules.map(m => (m.id === enhancedModule.id ? enhancedModule : m));
      } else {
        // Add new module
        return [...prevModules, enhancedModule];
      }
    });
  };

  // Unregister a module
  const unregisterModule = (moduleId: string) => {
    setModules(prevModules => prevModules.filter(m => m.id !== moduleId));

    // If the active module is being removed, clear it
    if (activeModuleId === moduleId) {
      setActiveModuleId(null);
    }
  };

  // Update module availability
  const updateModuleAvailability = (moduleId: string, isEnabled: boolean) => {
    setModules(prevModules => {
      return prevModules.map(m => (m.id === moduleId ? { ...m, enabled: isEnabled } : m));
    });
  };

  // Update module expandability
  const updateModuleExpandability = (moduleId: string, canExpand: boolean) => {
    setModules(prevModules => {
      return prevModules.map(m => (m.id === moduleId ? { ...m, expandEnabled: canExpand } : m));
    });
  };

  // Set module panel size
  const setModulePanelSize = (moduleId: string, size: number) => {
    setModulePanelSizes(prev => ({
      ...prev,
      [moduleId]: size,
    }));
  };

  // Get active module
  const getActiveModule = () => {
    if (!activeModuleId) return null;
    return modules.find(m => m.id === activeModuleId) || null;
  };

  // Subscribe to module registry changes
  useModuleRegistry(() => {
    // This will be called when modules are registered/unregistered
    const registeredModules = getModules();
    setModules(registeredModules);
  });

  // Initial load of modules
  useEffect(() => {
    const registeredModules = getModules();
    setModules(registeredModules);
  }, []);

  // Context value
  const value = {
    modules,
    registerModule,
    unregisterModule,
    activeModuleId,
    setActiveModuleId,
    isPanelVisible,
    setPanelVisible,
    panelSize,
    setPanelSize,
    isExpanded,
    setIsExpanded,
    modulePanelSizes,
    setModulePanelSize,
    getActiveModule,
    updateModuleAvailability,
    updateModuleExpandability,
    outsideCall,
    setOutsideCall,
  } as RightPanelContextType;

  return <RightPanelContext.Provider value={value}>{children}</RightPanelContext.Provider>;
};

export const useRightPanel = (): RightPanelContextType => {
  const context = useContext(RightPanelContext);
  if (!context) {
    throw new Error('useRightPanel must be used within a RightPanelProvider');
  }
  return context;
};
